import { ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { PlanDateAction, WIPType } from 'app/modules/well-integrity-planning/model';
import { WipService } from 'app/modules/well-integrity-planning/services/wip.service';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import module from "app/modules/well-integrity-planning/well-integrity-planning.module";
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { PlanDateFormComponent } from 'app/modules/well-integrity-planning/components/plan-date/plan-date.component';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';

@Component({
    selector: 'app-corrective-maintainance',
    templateUrl: './cm.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./cm.page.scss']
})
export class CorrectiveMaintainancePage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any
    startingIndex: number = 0
    isLoading: boolean = false

    wipType: WIPType = WIPType.cm
    downloadPath = {
        excel: "/v1/WellActivityPlanning/DownloadOverallCmExcel"
    }

    sortInfo: string = 'Created Date';
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        wellProductionStatus: 'Status',
        planDate: 'CM Plan Date',
        failureDate: 'Failure Date',
        remark: 'Remark',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
      }));
    
    
    constructor(
        private service: WipService,
        private _formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
    ) { }

    ngOnInit(): void {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort)
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page).pipe(
                switchMap(() => {
                    // this.isLoading = true;

                    let pageInfo = {
                        'count': this._paginator.length,
                        'pageSize': this._paginator.pageSize,
                        'limit': this._paginator.pageSize,
                        'offset': this._paginator.pageIndex
                    }
                    this.setPage(pageInfo);

                    return EMPTY
                }),
                map(() => {
                    this.isLoading = false;
                }),
            ).subscribe();
        }
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.startingIndex = this.dt.page.offset * this.dt.page.size

        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        const params = this.searchForm.value;

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.service
            .getCM({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.rows = pagedData.data.data;
            });
    }

    setSearch() {
        this.sortInfo = 'Field'
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [null],
            PlatformName: [null],
            StringName: [null],
            FieldName: [null],
            Year: [null],
        });

        this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0
        this.setPage({ offset: this.dt.page.offset });
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    onSort(sort: any) {
        if (!sort) return;

        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    handleFormatDate(dateString: string, withTime) {
        return handleFormatDate(dateString, withTime)
    }

    formDialog(payload = null) {
        let action: PlanDateAction = 'edit';
        if (!payload) {
            payload = {
                id: null,
                planDate: null,
                remark: null,
                wellStringId: null
            }

            action = 'create'
        }
        const dialogRef = this.dialog.open(PlanDateFormComponent, {
            data: {
                payload,
                type: this.wipType,
                action
            },
            width: '70%',
        });
        dialogRef
            .afterClosed()
            .subscribe((isUpdate) => {
                if (isUpdate) {
                    this.setPage({ offset: this.dt.page.offset })
                }
            });
    }

    deleteRecord(payload) {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete CM Record',
            message: 'Are you sure you want to delete this record?',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                this.service.deleteOtherActivity(payload.id)
                    .subscribe((response) => {
                        this.setPage({ offset: this.dt.page.offset })
                    });
            }
        })
    }
}
